import React, { useState, useEffect, useContext } from "react";

import { useHistory, useLocation } from "react-router";
import { Link } from "react-router-dom";
import { isEmpty } from "lodash";

import Form from "../../components/Forms/Form";
import UserContext from "../../lib/UserContext";
import UserSchemas from "../../schemas/user.schema";
import LookupsAPI from "../../API/lookups.api";
import UsersAPI from "../../API/users.api";
import { SuccessAlert, ErrorAlert } from "../../utils/Alerts";
import { getRoles } from "../../utils/OktaHelpers";
import { ACCESS_OPTIONS, ROLES, ROLES_VERSE } from "../../utils/constants";
import { isAuthorized } from "../../utils/RolesHelpers";
import { Trans, useTranslation } from "react-i18next";
import UserAccessForm from "../../components/Forms/UserAccessForm";
import SimpleNotification from "../../components/Notifications/SimpleNotification";
import LogCapture from "../../utils/LogManagement/LogHelper";
import BreadCrumb from "../../components/Navbars/BreadCrumb";
import { validate } from "../../utils/ValidationHelpers";
import MainLoadingButton from "../../components/Buttons/MainLoadingButton";
import UserOrganizationForm from "../../components/Forms/UserOrganizationForm";
import ValidationError from "../../components/Shared/ValidationError";

const UserForm = () => {
    const [isFormValid, setIsFormValid] = useState(false);
    const [organizations, setOrganizations] = useState([]);
    const [roles, setRoles] = useState([]);
    const [currentUserRole, setCurrentUserRole] = useState([]);
    const [, setIsLoading] = useState(true);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isRequestingResetPassword, setIsRequestingResetPassword] =
        useState(false);
    const [isRequestingUnlockUser, setIsRequestingUnlockUser] = useState(false);
    const [isUserLocked, setIsUserLocked] = useState(false);
    const [isPasswordRequired, setIsPasswordRequired] = useState(true);
    const [isGrantedModelAccess, setIsGrantedModelAccess] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState({});
    const [selectedModels, setSelectedModels] = useState([]);
    const [mappedModelAccess, setMappedModelAccess] = useState(null);
    const [mappedSecondaryOrganizations, setMappedSecondaryOrganizations] =
        useState({});
    const [isOrganizationDefaultChecked, setIsOrganizationDefaultChecked] =
        useState("");
    const [selectedOrganizations, setSelectedOrganizations] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);

    const [input, setInput] = useState({});
    const context = useContext(UserContext);

    const history = useHistory();
    const propCurrentUserRole = getRoles();

    const { t } = useTranslation();
    const [newFields, setNewFields] = useState({
        organization: {
            label: t("affiliate"),
            name: "organizationId",
            type: "Dropdown",
            options: [],
            role: !isAuthorized(
                [ROLES.partnerAdmin, ROLES.support],
                getRoles()
            ),
            placeholder: t("selectPlaceholder", { entity: t("affiliate") }),
            required: true,
            order: 3,
        },
        email: {
            label: t("email"),
            name: "email",
            type: "Input",
            placeholder: t("emailPlaceholder"),
            required: true,
            order: 4,
        },
        password: {
            label: t("password"),
            name: "password",
            placeholder: t("passwordPlaceholder"),
            type: "password",
            showPasswordIcon: true,
            required: true,
            order: 5,
        },
        passwordConfirmation: {
            label: t("passwordConfirmation"),
            name: "passwordConfirmation",
            placeholder: t("passwordConfirmationPlaceholder"),
            type: "password",
            showPasswordIcon: true,
            required: true,
            order: 6,
        },
        role: {
            label: t("role"),
            name: "role",
            type: "Dropdown",
            options: [],
            role: !isAuthorized(
                [ROLES.partnerAdmin, ROLES.support],
                getRoles()
            ),
            placeholder: t("selectPlaceholder", { entity: t("role") }),
            required: true,
            order: 7,
        },
    });

    const [editFields, setEditFields] = useState({
        firstname: {
            label: t("firstName"),
            name: "firstname",
            type: "Input",
            placeholder: t("firstNamePlaceholder"),
            required: true,
            order: 1,
        },
        lastname: {
            label: t("lastName"),
            name: "lastname",
            type: "Input",
            placeholder: t("lastNamePlaceholder"),
            required: true,
            order: 2,
        },
        role: {
            label: t("role"),
            name: "role",
            type: "Dropdown",
            options: [],
            role: !isAuthorized([ROLES.support], getRoles()),
            placeholder: t("selectPlaceholder", { entity: t("role") }),
            required: true,
            order: 8,
        },
    });
    const [fields, setFields] = useState([]);

    const breadCrumbs = [
        {
            path: "/dashboard",
            element: () => <i className="fas fa-home" />,
        },
        {
            path: "/users",
            element: () => <Trans i18nKey="headerUser">Users</Trans>,
        },
        {
            element: () => (data ? t("buttonEditUser") : t("buttonAddUser")),
        },
    ];

    const location = useLocation();

    useEffect(() => {
        const propData = location.state?.data || null;
        setData(propData);
        const currentUser = context.getUser();
        setCurrentUser(currentUser);
        setCurrentUserRole(propCurrentUserRole);
        getOrganizations();
        getRolesLookup().then((result) => {
            setRoles(result);
            const userRoleId = result.find((record) => {
                return record.role === ROLES.user;
            });

            if (propData) {
                let role;
                // fill roles in case of support
                if (isAuthorized([ROLES.support], propCurrentUserRole)) {
                    let fields = { ...editFields };
                    const newRoles = result.filter(
                        (item) =>
                            item.role === ROLES_VERSE[propData.role] ||
                            item.role === propData.role
                    );
                    fields.role.options = newRoles.map((r) => {
                        if (r.role === propData.role) role = r.id;
                        return {
                            value: r.id,
                            label: r.role,
                        };
                    });
                    setEditFields(editFields);
                }
                setInput({ ...propData, role });
            } else {
                let modifiedResult = {
                    organizationId: currentUser.organization?.id,
                };
                //Check if the creator is admin set created account Role = user by default
                if (isAuthorized([ROLES.admin], propCurrentUserRole)) {
                    modifiedResult.role = userRoleId?.id || "";
                }
                setInput(modifiedResult);
            }
        });
    }, []);

    useEffect(() => {
        let newRoles = [];
        let allowedRoles = [];
        const orgId = currentUser?.organization?.id;

        if (isAuthorized([ROLES.partnerAdmin], currentUserRole)) {
            if (input.organizationId === orgId) {
                allowedRoles = [ROLES.partnerAdmin, ROLES.partnerUser];
            } else {
                allowedRoles = [ROLES.user, ROLES.admin];
            }
        } else if (isAuthorized([ROLES.superAdmin], currentUserRole)) {
            allowedRoles = [
                ROLES.partnerUser,
                ROLES.partnerAdmin,
                ROLES.support,
            ];
        } else if (isAuthorized([ROLES.support], currentUserRole)) {
            if (input.organizationId === orgId) {
                allowedRoles = [ROLES.partnerAdmin, ROLES.partnerUser];
            } else {
                allowedRoles = [ROLES.user, ROLES.admin];
            }
        }
        newRoles = roles.filter((item) => allowedRoles.includes(item.role));
        let fields = { ...newFields };
        fields.role.options = newRoles.map((role) => ({
            value: role.id,
            label: role.role,
        }));
        setNewFields(fields);
    }, [input.organizationId]);

    useEffect(() => {
        mapOrganizationsLookupOptions();
    }, [organizations]);

    useEffect(() => {
        if (data?.id) {
            getIsUserLocked();
        }
    }, [data]);

    useEffect(() => {
        if (data?.userOrganizations) {
            setMappedSecondaryOrganizations(
                mapSecondaryOrganizations(data?.userOrganizations)
            );
        }
    }, [data]);

    useEffect(() => {
        let sortedFields = [];
        if (data) {
            sortedFields = Object.values(editFields);
        } else {
            sortedFields = Object.values({ ...editFields, ...newFields });
        }
        sortedFields.sort((a, b) => (a.order < b.order ? -1 : 1));
        setFields(sortedFields);
    }, [data]);

    useEffect(() => {
        localValidation();
    }, [input, isPasswordRequired]);

    const mapSecondaryOrganizations = (organizations) => {
        const dict = {};
        organizations.forEach((organization) => {
            dict[organization.organizationId] = organization;
        });
        return dict;
    };

    const mapOrganizationsLookupOptions = () => {
        let fields = { ...newFields };
        let organizationOptions =
            organizations &&
            organizations.map((org) => ({
                value: org.id,
                label: `${org.name} - (${org.currentActiveUsers}/${org.maxUsersNumber})`,
            }));
        fields.organization.options = organizationOptions;
        setNewFields(fields);
    };

    const getOrganizations = async () => {
        LookupsAPI.getOrganizations()
            .then((result) => {
                setOrganizations(result);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const getIsUserLocked = async () => {
        const result = await UsersAPI.getIsUserLocked(data.id);
        setIsUserLocked(result);
    };

    const unlockUser = async () => {
        setIsRequestingUnlockUser(true);
        const result = await UsersAPI.unlockUser(data.id);
        setIsUserLocked(!result);
        setIsRequestingUnlockUser(false);
    };

    const getRolesLookup = async () => {
        const result = await LookupsAPI.getRoles();
        setIsLoading(false);
        return result;
    };

    const localValidation = () => {
        const modifiedInputs = { ...input };
        if (!isPasswordRequired) {
            delete modifiedInputs.password;
            delete modifiedInputs.passwordConfirmation;
        }

        validate(
            modifiedInputs,
            data ? UserSchemas.EditUserSchema : UserSchemas.AddUserSchema,
            setIsFormValid,
            setError,
            {
                abortEarly: false,
                allowUnknown: true,
                context: {
                    isPasswordRequired,
                    isSuperAdmin: isSuperAdmin(),
                },
            }
        );
    };

    const handleSubmitNewUser = async () => {
        setIsSubmitting(true);
        setIsFormSubmitted(true);
        if (
            !isFormValid ||
            (isSuperAdmin() && isEmpty(selectedOrganizations))
        ) {
            setIsSubmitting(false);
            return;
        }

        const userData = {
            ...input,
            isPasswordRequired,
            roleId: input.role,
            grantAccessForModels: isGrantedModelAccess,
            ...getDefaultSelectedOrganization(),
            userOrganizations: getCreateUserOrganizationPayload(),
        };
        delete userData.passwordConfirmation;
        delete userData.role;

        try {
            const result = await UsersAPI.addNewUser(userData);
            if (result) {
                history.push("/users");
                SuccessAlert(
                    t(
                        isPasswordRequired
                            ? "addUserSuccessMessage"
                            : "addUserWithoutPasswordSuccessMessage"
                    )
                );
                LogCapture.info(`User created with email: ${input?.email}`);
            }
        } catch (responseError) {
            ErrorAlert(`${responseError}`);
            LogCapture.error(
                `Couldn't create user with email: ${input?.email}, error: ${responseError}`
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const getDefaultSelectedOrganization = () => {
        let selected = {};

        if (isSuperAdmin()) {
            selected = selectedOrganizations.find(
                (organization) =>
                    organization.value.id === isOrganizationDefaultChecked
            )?.value;
        }

        return {
            roleId: selected?.newRole?.value || input.role,
            organizationId: selected?.id || input.organizationId,
        };
    };

    const getNewMappedUserAccess = () => {
        return selectedModels.map((access) => {
            const accessValue = access.value;
            const accessType = accessValue?.write
                ? ACCESS_OPTIONS.write.value
                : ACCESS_OPTIONS.read.value;
            const model = mappedModelAccess[accessValue?.id];

            if (model && accessValue.isDeleted) {
                return {
                    modelId: accessValue?.id,
                    isDeleted: accessValue?.isDeleted,
                };
            } else if (model !== accessType || !model) {
                return {
                    modelId: accessValue?.id,
                    read: accessValue?.read || false,
                    write: accessValue?.write || false,
                };
            }
        });
    };

    const getCreateUserOrganizationPayload = () => {
        return selectedOrganizations
            .filter(
                (organization) =>
                    organization.value.id !== isOrganizationDefaultChecked
            )
            .map((org) => {
                const organizationData = org.value;
                return {
                    roleId: organizationData.newRole.value,
                    organizationId: organizationData.id,
                    partnerId: organizationData.partnerId,
                };
            });
    };

    const getEditUserOrganizationsPayload = () => {
        return selectedOrganizations
            .map((org) => {
                const organizationData = org.value;
                const organizationId = organizationData.id;

                const organizationMapData =
                    mappedSecondaryOrganizations[organizationId];

                if (organizationData.isDeleted) {
                    return {
                        id: organizationMapData.id,
                        organizationId: organizationId,
                        isDeleted: true,
                    };
                } else if (!organizationMapData) {
                    return {
                        organizationId: organizationId,
                        roleId: organizationData.newRole.value,
                        partnerId: organizationData.partnerId,
                        isCreated: true,
                    };
                } else if (
                    organizationMapData &&
                    organizationMapData.role !==
                        organizationData?.newRole?.label
                ) {
                    return {
                        id: organizationMapData.id,
                        roleId: organizationData?.newRole?.value,
                    };
                }
            })
            .filter(Boolean);
    };

    const handleSubmitEditUser = async () => {
        setIsSubmitting(true);
        setIsFormSubmitted(true);
        if (!isFormValid) {
            setIsSubmitting(false);
            return;
        }

        const userData = {
            id: input.id,
            firstname: input.firstname,
            lastname: input.lastname,
            modelUserAccesses: getNewMappedUserAccess(),
            userOrganizations: getEditUserOrganizationsPayload(),
            image: input.image,
            roleId: input.role,
        };

        try {
            const result = await UsersAPI.updateUser(userData);
            if (result) {
                history.push("/users");
                SuccessAlert(t("editUserSuccessMessage"));
                LogCapture.info(`User updated with email: ${input?.email}`);
            }
        } catch (responseError) {
            LogCapture.error(
                `Couldn't update user with email: ${input?.email}, error: ${responseError}`
            );
            ErrorAlert(`Error: ${responseError}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleIsPasswordRequiredChange = () => {
        let fields = { ...newFields };
        fields.password.disabled = !!isPasswordRequired;
        fields.passwordConfirmation.disabled = !!isPasswordRequired;
        setNewFields(fields);

        setIsPasswordRequired(!isPasswordRequired);
    };

    const handleGrantAccessChange = () => {
        setIsGrantedModelAccess(!isGrantedModelAccess);
    };

    const isPartnerAdmin = () => {
        return data?.role === ROLES.partnerAdmin;
    };

    const isSuperAdmin = () => {
        return isAuthorized([ROLES.superAdmin], currentUserRole);
    };

    const renderModelsAccess = () => {
        const isValid = isPartnerAdmin();
        const allowedRoles = [
            ROLES.support,
            ROLES.partnerAdmin,
            ROLES.superAdmin,
        ];

        if (isAuthorized(allowedRoles, currentUserRole)) {
            if (isValid) {
                return (
                    <SimpleNotification message="You cannot edit Models access for this user! Partner Admins already have access to all models." />
                );
            } else if (data) {
                return (
                    <UserAccessForm
                        user={data}
                        selectedModels={selectedModels}
                        setSelectedModels={setSelectedModels}
                        mappedModelAccess={mappedModelAccess}
                        setMappedModelAccess={setMappedModelAccess}
                    />
                );
            }
        }
    };

    const renderUserOrganizations = () => {
        if (isSuperAdmin()) {
            return (
                <div className="pb-3">
                    <UserOrganizationForm
                        isChecked={isOrganizationDefaultChecked}
                        setIsChecked={setIsOrganizationDefaultChecked}
                        excludedOrganization={
                            data && {
                                label: data.organization,
                                value: data.organizationId,
                                role: data.role,
                            }
                        }
                        notificationMessage={
                            data && t("defaultOrganizationMessage")
                        }
                        selectedOrganizations={selectedOrganizations}
                        setSelectedOrganizations={setSelectedOrganizations}
                        mappedOrganizations={mappedSecondaryOrganizations}
                    />
                    <ValidationError
                        errorMessage={
                            isFormSubmitted &&
                            isEmpty(selectedOrganizations) &&
                            t("defaultOrganizationErrorMessage")
                        }
                    />
                </div>
            );
        }
    };

    const handleResetPassword = () => {
        setIsRequestingResetPassword(true);

        UsersAPI.resetUserPassword(input.id)
            .then((res) => {
                if (res) {
                    SuccessAlert(
                        `Reset password request has been sent to ${input.firstname} via email successfully!`
                    );
                    LogCapture.info(
                        `Password reset requested for ${input?.email}`
                    );
                } else {
                    ErrorAlert(
                        `Unable to send reset password request to ${input?.firstname}`
                    );
                    LogCapture.error(
                        `Couldn't request password reset for ${input?.email}`
                    );
                }
            })
            .finally(() => {
                setIsRequestingResetPassword(false);
            });
    };

    const renderSupportButtons = () => {
        const allowedRoles = [
            ROLES.support,
            ROLES.partnerAdmin,
            ROLES.superAdmin,
        ];

        if (isAuthorized(allowedRoles, currentUserRole) && !isEmpty(data)) {
            return (
                <div className="flex">
                    <MainLoadingButton
                        buttonText={t("unlockUser")}
                        isRequesting={isRequestingUnlockUser}
                        isDisabled={!isUserLocked}
                        onClick={unlockUser}
                        iconClassName="fas fa-unlock"
                        className="mr-2"
                    />
                    <MainLoadingButton
                        buttonText={t("resetPassword")}
                        isRequesting={isRequestingResetPassword}
                        onClick={handleResetPassword}
                    />
                </div>
            );
        }
    };

    return (
        <>
            <BreadCrumb crumbs={breadCrumbs} />
            <header className="main-content-header flex items-center justify-between">
                <div className="main-customer-form-header action-header-wrapper flex items-center">
                    <i
                        className={`fas ${
                            data ? "fa-user-edit" : "fa-user-plus"
                        }`}
                    ></i>
                    <h2 className="h2">
                        {data ? t("buttonEditUser") : t("addNewUser")}
                    </h2>
                </div>
                <div>{renderSupportButtons()}</div>
            </header>
            <div className="card-table shadow">
                <Form
                    fields={fields}
                    input={input}
                    setInput={setInput}
                    error={error}
                    isFormSubmitted={isFormSubmitted}
                    addResetButton={false}
                />
                {renderUserOrganizations()}
                {renderModelsAccess()}

                <div className="main-customer-form-bnt flex items-center justify-between">
                    <div className="flex flex-start">
                        {!data && (
                            <div className="checkbox-wrapper-input">
                                <input
                                    onChange={handleIsPasswordRequiredChange}
                                    type="checkbox"
                                    className="checkbox-element"
                                    id="isPasswordRequired"
                                    name="isPasswordRequired"
                                    value={isPasswordRequired}
                                    checked={isPasswordRequired}
                                />
                                <label htmlFor="isPasswordRequired">
                                    <Trans i18nKey="passwordOnBehalf">
                                        Create password on behalf of the user.
                                    </Trans>
                                </label>
                            </div>
                        )}
                        {!data && (
                            <div className="checkbox-wrapper-input ml-3">
                                <input
                                    onChange={handleGrantAccessChange}
                                    type="checkbox"
                                    className="checkbox-element"
                                    id="grantModelAccess"
                                    name="isPasswordRequired"
                                    value={isGrantedModelAccess}
                                    checked={isGrantedModelAccess}
                                />
                                <label htmlFor="grantModelAccess">
                                    <Trans i18nKey="accessOnBehalf">
                                        Grant access to all available models.
                                    </Trans>
                                </label>
                            </div>
                        )}
                    </div>
                    <div>
                        <Link to="/users">
                            <button className="secondary-blue-btn mr-2">
                                <Trans i18nKey="cancel">Cancel</Trans>
                            </button>
                        </Link>

                        <MainLoadingButton
                            buttonText={data ? t("edit") : t("add")}
                            isRequesting={isSubmitting}
                            onClick={
                                data
                                    ? handleSubmitEditUser
                                    : handleSubmitNewUser
                            }
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default UserForm;
